import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { ShoppingCart, Wrench, Award, Package, TrendingUp, BarChart3, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const QCDashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQCType, setSelectedQCType] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      // Mock data - replace with real API
      const mockData = {
        pqc: {
          total: 150,
          today: 12,
          grades: {
            'A+': 18, 'A': 27, 'A-': 30,
            'B+': 22, 'B': 15, 'B-': 12,
            'C+': 9, 'C': 7, 'C-': 5,
            'D': 3, 'E': 2
          },
          avg_buyback_price: 15000,
          total_buyback_value: 2250000
        },
        iqc: {
          total: 200,
          today: 18,
          pending_parts: 15,
          critical_devices: 5,
          severity_breakdown: {
            'NTF': 450,
            'Low': 180,
            'Medium': 120,
            'High': 45,
            'Critical': 5
          },
          top_parts_needed: [
            { part: 'Display', count: 25 },
            { part: 'Battery', count: 18 },
            { part: 'Charging Port', count: 12 },
            { part: 'Camera', count: 8 }
          ]
        },
        fqc: {
          total: 180,
          today: 15,
          pass_rate: 92,
          grades: {
            'A+': 22, 'A': 35, 'A-': 38,
            'B+': 28, 'B': 20, 'B-': 15,
            'C+': 10, 'C': 7, 'C-': 3,
            'D': 2, 'E': 0
          },
          rework_needed: 8
        },
        oqc: {
          total: 175,
          today: 14,
          ready_for_dispatch: 170,
          issues_found: 5
        }
      };
      setData(mockData);
    } catch (error) {
      console.error('Error fetching QC data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'bg-green-600', 'A': 'bg-green-500', 'A-': 'bg-green-400',
      'B+': 'bg-blue-500', 'B': 'bg-blue-400', 'B-': 'bg-blue-300',
      'C+': 'bg-yellow-500', 'C': 'bg-yellow-400', 'C-': 'bg-yellow-300',
      'D': 'bg-orange-500', 'E': 'bg-red-600'
    };
    return colors[grade] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading QC Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <BarChart3 className="w-8 h-8 mr-3" />
            QC Management Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive analytics for PQC, IQC, FQC, and OQC operations
          </p>
        </div>

        {/* QC Type Selector */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex gap-3">
            <button
              onClick={() => setSelectedQCType('all')}
              className={`flex-1 p-4 rounded-lg font-medium transition-all ${
                selectedQCType === 'all'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedQCType === 'all' ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              <div className="text-2xl mb-1">📊</div>
              <div className="text-sm font-semibold">All QC Types</div>
            </button>
            <button
              onClick={() => setSelectedQCType('pqc')}
              className={`flex-1 p-4 rounded-lg font-medium transition-all ${
                selectedQCType === 'pqc'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedQCType === 'pqc' ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              <ShoppingCart className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm font-semibold">PQC (Buyback)</div>
            </button>
            <button
              onClick={() => setSelectedQCType('iqc')}
              className={`flex-1 p-4 rounded-lg font-medium transition-all ${
                selectedQCType === 'iqc'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedQCType === 'iqc' ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              <Wrench className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm font-semibold">IQC (Diagnosis)</div>
            </button>
            <button
              onClick={() => setSelectedQCType('fqc')}
              className={`flex-1 p-4 rounded-lg font-medium transition-all ${
                selectedQCType === 'fqc'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedQCType === 'fqc' ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              <Award className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm font-semibold">FQC (Final Grade)</div>
            </button>
            <button
              onClick={() => setSelectedQCType('oqc')}
              className={`flex-1 p-4 rounded-lg font-medium transition-all ${
                selectedQCType === 'oqc'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedQCType === 'oqc' ? { backgroundColor: ZOBOX_BLUE } : {}}
            >
              <Package className="w-6 h-6 mx-auto mb-1" />
              <div className="text-sm font-semibold">OQC (Outgoing)</div>
            </button>
          </div>
        </div>

        {/* ALL QC TYPES VIEW */}
        {selectedQCType === 'all' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                <ShoppingCart className="w-8 h-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{data.pqc.total}</div>
                <div className="text-sm opacity-90">PQC Completed</div>
                <div className="text-xs mt-2">Today: {data.pqc.today}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                <Wrench className="w-8 h-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{data.iqc.total}</div>
                <div className="text-sm opacity-90">IQC Completed</div>
                <div className="text-xs mt-2">Today: {data.iqc.today}</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                <Award className="w-8 h-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{data.fqc.total}</div>
                <div className="text-sm opacity-90">FQC Completed</div>
                <div className="text-xs mt-2">Today: {data.fqc.today}</div>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
                <Package className="w-8 h-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold">{data.oqc.total}</div>
                <div className="text-sm opacity-90">OQC Completed</div>
                <div className="text-xs mt-2">Today: {data.oqc.today}</div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                  PQC - Buyback Overview
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Buyback Price</span>
                    <span className="font-bold">₹{data.pqc.avg_buyback_price.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Value</span>
                    <span className="font-bold text-green-600">₹{data.pqc.total_buyback_value.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                  IQC - Diagnosis Status
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending Parts</span>
                    <span className="font-bold text-yellow-600">{data.iqc.pending_parts}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Critical Devices</span>
                    <span className="font-bold text-red-600">{data.iqc.critical_devices}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                  FQC - Quality Status
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pass Rate</span>
                    <span className="font-bold text-green-600">{data.fqc.pass_rate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rework Needed</span>
                    <span className="font-bold text-orange-600">{data.fqc.rework_needed}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* PQC VIEW */}
        {selectedQCType === 'pqc' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Total PQC</h3>
                <div className="text-4xl font-bold">{data.pqc.total}</div>
                <div className="text-sm text-gray-600 mt-1">Today: {data.pqc.today}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Avg Buyback Price</h3>
                <div className="text-4xl font-bold text-green-600">₹{data.pqc.avg_buyback_price.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Total Value</h3>
                <div className="text-4xl font-bold text-blue-600">₹{(data.pqc.total_buyback_value / 100000).toFixed(1)}L</div>
              </div>
            </div>

            {/* Grade Distribution */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                PQC Grade Distribution
              </h3>
              <div className="space-y-2">
                {Object.entries(data.pqc.grades).map(([grade, count]) => {
                  const total = Object.values(data.pqc.grades).reduce((a, b) => a + b, 0);
                  const percentage = ((count / total) * 100).toFixed(1);
                  return (
                    <div key={grade} className="flex items-center gap-3">
                      <div className="w-16 text-sm font-semibold">{grade}</div>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className={`h-full ${getGradeColor(grade)} flex items-center justify-end px-2 text-white text-xs font-bold transition-all`}
                          style={{ width: `${percentage}%` }}
                        >
                          {percentage > 5 && `${count}`}
                        </div>
                      </div>
                      <div className="w-20 text-sm text-gray-600 text-right">
                        {count} ({percentage}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* IQC VIEW */}
        {selectedQCType === 'iqc' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Total IQC</h3>
                <div className="text-3xl font-bold">{data.iqc.total}</div>
                <div className="text-xs text-gray-500 mt-1">Today: {data.iqc.today}</div>
              </div>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Pending Parts</h3>
                <div className="text-3xl font-bold text-yellow-600">{data.iqc.pending_parts}</div>
              </div>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Critical Devices</h3>
                <div className="text-3xl font-bold text-red-600">{data.iqc.critical_devices}</div>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">No Issues (NTF)</h3>
                <div className="text-3xl font-bold text-green-600">{data.iqc.severity_breakdown.NTF}</div>
              </div>
            </div>

            {/* Severity Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                IQC Severity Breakdown
              </h3>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(data.iqc.severity_breakdown).map(([severity, count]) => {
                  const colors = {
                    'NTF': 'bg-green-100 border-green-300 text-green-800',
                    'Low': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                    'Medium': 'bg-orange-100 border-orange-300 text-orange-800',
                    'High': 'bg-red-100 border-red-300 text-red-800',
                    'Critical': 'bg-purple-100 border-purple-300 text-purple-800'
                  };
                  const icons = { 'NTF': '✅', 'Low': '🟡', 'Medium': '🟠', 'High': '🔴', 'Critical': '🚨' };
                  return (
                    <div key={severity} className={`border-2 rounded-lg p-4 ${colors[severity]}`}>
                      <div className="text-2xl mb-1">{icons[severity]}</div>
                      <div className="text-xs font-semibold uppercase">{severity}</div>
                      <div className="text-2xl font-bold">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Parts Needed */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                Top Parts Needed
              </h3>
              <div className="space-y-3">
                {data.iqc.top_parts_needed.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{item.part}</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                      {item.count} devices
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* FQC VIEW */}
        {selectedQCType === 'fqc' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Total FQC</h3>
                <div className="text-3xl font-bold">{data.fqc.total}</div>
                <div className="text-xs text-gray-500 mt-1">Today: {data.fqc.today}</div>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Pass Rate</h3>
                <div className="text-3xl font-bold text-green-600">{data.fqc.pass_rate}%</div>
              </div>
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Rework Needed</h3>
                <div className="text-3xl font-bold text-orange-600">{data.fqc.rework_needed}</div>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Ready to Ship</h3>
                <div className="text-3xl font-bold text-blue-600">{data.fqc.total - data.fqc.rework_needed}</div>
              </div>
            </div>

            {/* Grade Distribution */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
                FQC Final Grade Distribution
              </h3>
              <div className="space-y-2">
                {Object.entries(data.fqc.grades).map(([grade, count]) => {
                  const total = Object.values(data.fqc.grades).reduce((a, b) => a + b, 0);
                  const percentage = ((count / total) * 100).toFixed(1);
                  return (
                    <div key={grade} className="flex items-center gap-3">
                      <div className="w-16 text-sm font-semibold">{grade}</div>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className={`h-full ${getGradeColor(grade)} flex items-center justify-end px-2 text-white text-xs font-bold transition-all`}
                          style={{ width: `${percentage}%` }}
                        >
                          {percentage > 5 && `${count}`}
                        </div>
                      </div>
                      <div className="w-20 text-sm text-gray-600 text-right">
                        {count} ({percentage}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* OQC VIEW */}
        {selectedQCType === 'oqc' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Total OQC</h3>
                <div className="text-3xl font-bold">{data.oqc.total}</div>
                <div className="text-xs text-gray-500 mt-1">Today: {data.oqc.today}</div>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Ready for Dispatch</h3>
                <div className="text-3xl font-bold text-green-600">{data.oqc.ready_for_dispatch}</div>
              </div>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Issues Found</h3>
                <div className="text-3xl font-bold text-red-600">{data.oqc.issues_found}</div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">OQC - Final Look & Feel Check</h3>
                  <p className="text-sm text-blue-800">
                    OQC focuses on presentation quality: charging status, cleanliness, IMEI labels, 
                    fingerprint-free surfaces, and packaging quality. Simple checklist to ensure devices 
                    are ready for customer delivery.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default QCDashboard;
